'use client';
import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const COLORS = {
  ACCENT_GOLD: '#faaf40',
  DARK_BG: '#101010',
  LIGHT_TEXT: '#f0f0f0',
};

const mealPlans = [
  {
    title: 'BALANCED PLAN',
    image: '/ImageGallery/CardioElite2.webp',
    highlight: 'Daily Energy & Wellness',
    desc: 'Perfectly portioned meals crafted to balance carbs, proteins, and healthy fats for all-day vitality. **Stay energized** and maintain your ideal weight effortlessly.',
    features: ['3 meals + 1 snack per day', 'Approx. 1700 kcal', 'Carbs 45% • Protein 30% • Fats 25%'],
  },
  {
    title: 'HIGH-PROTEIN PLAN',
    image: '/ImageGallery/ArtofAtmosphere1.webp',
    highlight: 'Muscle Growth & Recovery',
    desc: 'Engineered for fitness-focused individuals. **Boost performance** with lean protein meals designed to sculpt and strengthen your body naturally.',
    features: ['3 protein-rich meals', 'Approx. 2000 kcal', 'Carbs 35% • Protein 45% • Fats 20%'],
  },
  {
    title: 'VEGAN PLAN',
    image: '/ImageGallery/PilatesStudio1.webp',
    highlight: 'Plant-Based Vitality',
    desc: 'Clean, nutrient-dense meals from organic sources. **Rebuild and recharge** your system with complete plant protein and fiber-rich ingredients.',
    features: ['3 vegan meals + smoothie', 'Approx. 1600 kcal', 'Carbs 50% • Protein 25% • Fats 25%'],
  },
  {
    title: 'KETO PLAN',
    image: '/ImageGallery/ArtofAtmosphere2.webp',
    highlight: 'Fat-Burning Optimization',
    desc: 'Low-carb, high-fat plan designed to promote ketosis and **accelerate fat loss** while maintaining mental clarity and sustained energy.',
    features: ['3 keto meals + coffee', 'Approx. 1500 kcal', 'Carbs 10% • Protein 30% • Fats 60%'],
  },
];

const renderDescription = (text) =>
  text.split('**').map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="text-white font-semibold">
        {part}
      </strong>
    ) : (
      part
    )
  );

function PlanCard({ plan, index, total }) {
  const ref = useRef(null);
  
  // Adjusted offset for smoother mobile scrolling
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  // Animations
  const y = useTransform(scrollYProgress, [0, 0.5, 1], ['10%', '0%', '-10%']); // Reduced movement for mobile stability
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.95, 1, 0.95]); // Subtler scale
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.9, 1], [0, 1, 1, 0]);

  return (
    <motion.div
      ref={ref}
      // Sticky positioning:
      // Mobile: top-28 (clears the smaller header)
      // Desktop: top-32 (clears the larger header)
      className="sticky top-28 md:top-32"
      style={{
        y,
        scale,
        opacity,
        zIndex: total - index,
        transformOrigin: 'center center',
      }}
    >
      <div
        // Layout:
        // Mobile: Flex-col (Image Top, Text Bottom)
        // Desktop: Flex-row (Side by Side)
        // Logic: index % 2 check only applies to MD screens for zig-zag effect
        className={`
            flex flex-col md:flex-row items-center gap-6 md:gap-10 
            py-8 md:py-16 px-4 md:px-0
            rounded-3xl bg-[#0f0f0f]/90 md:bg-transparent backdrop-blur-md md:backdrop-blur-none border md:border-none border-white/5
            ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}
        `}
      >
        {/* Image Container */}
        <div className="w-full md:w-1/2 relative flex justify-center items-center h-64 sm:h-80 md:h-[28rem] overflow-hidden rounded-2xl group shadow-2xl">
          <div
            className="absolute inset-0 rounded-2xl z-0"
            style={{
              background: '#1a1a1a',
              boxShadow: `0 0 25px rgba(0,0,0,0.8), inset 0 0 20px rgba(250,175,64,0.4)`,
            }}
          />
          <img
            src={plan.image}
            alt={plan.title}
            className="absolute inset-0 w-full h-full object-cover rounded-2xl transition-transform duration-700 ease-out transform group-hover:scale-110 group-hover:brightness-110"
          />
        </div>

        {/* Text Container */}
        <div className="w-full md:w-1/2 text-left space-y-4 md:space-y-5 z-10">
          <p
            className="text-xs sm:text-sm uppercase tracking-widest font-medium"
            style={{ color: COLORS.ACCENT_GOLD }}
          >
            {plan.highlight}
          </p>

          <h3 className="text-2xl sm:text-4xl font-extrabold tracking-tight text-white">
            {plan.title}
          </h3>

          <p className="text-base sm:text-lg text-gray-400 leading-relaxed">
            {renderDescription(plan.desc)}
          </p>

          <ul className="space-y-2 pt-2 text-sm sm:text-base text-gray-300">
            {plan.features.map((f, i) => (
              <li key={i} className="flex items-start gap-2">
                <span style={{ color: COLORS.ACCENT_GOLD }} className="mt-1">•</span> 
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <div className="pt-4">
            <button
              className="w-full sm:w-auto px-8 py-3 rounded-full border-2 text-sm sm:text-md font-bold transition duration-300 hover:shadow-[0_0_15px_rgba(250,175,64,0.9)] hover:text-black"
              style={{
                borderColor: COLORS.ACCENT_GOLD,
                color: COLORS.ACCENT_GOLD,
                background: 'transparent',
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = `linear-gradient(to right, ${COLORS.ACCENT_GOLD}, #d4af37)`)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = 'transparent')
              }
            >
              VIEW PLAN
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function PlansDisplay() {
  const total = mealPlans.length;

  return (
    <section
      className="relative bg-gradient-to-b from-black via-[#0a0a0a] to-black text-white overflow-hidden pb-24"
      style={{
        perspective: '1200px',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        
        {/* Sticky Header */}
        <div className="text-center sticky top-0 bg-black/80 backdrop-blur-lg py-6 md:py-10 z-50 border-b border-white/5 md:border-none transition-all">
          <p
            className="text-xs sm:text-sm uppercase tracking-[0.2em] sm:tracking-[0.3em] font-medium mb-2"
            style={{ color: COLORS.ACCENT_GOLD }}
          >
            EXCLUSIVE DIET PROGRAMS
          </p>
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight">
            Tailored <span style={{ color: COLORS.ACCENT_GOLD }}>Meal Plans</span>
          </h2>
        </div>

        {/* Scroll Cards Container */}
        {/* Added extra padding bottom to ensure last card clears */}
        <div className="relative mt-4 md:mt-10 flex flex-col gap-12 md:gap-0"> 
          {mealPlans.map((plan, i) => (
            <PlanCard key={plan.title} plan={plan} index={i} total={total} />
          ))}
        </div>
        
      </div>
    </section>
  );
}